import React, { useState, useEffect, useRef } from 'react';
import { DollarSign, Save, RefreshCw, AlertTriangle, X, CheckCircle, Edit, Trash2, Users, Search, ChevronDown, List, Download } from 'lucide-react';
import * as XLSX from 'xlsx';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  Menu,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import MainService from '../../services/MainService';
import SearchableSelect from '../ui/SearchableSelect';

const DriverCommission = () => {
  const [commissions, setCommissions] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCommission, setSelectedCommission] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');

  const [commission, setCommission] = useState({
    Id: 0,
    PickUpValue: 0,
    DeliveryValue: 0,
    IsPickUpPercent: false,
    IsDeliveryPercent: false,
    IsByArea: false,
    EmployeeId: ''
  });

  useEffect(() => {
    loadCommissions();
    loadEmployees();
  }, [currentPage, pageSize]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const loadEmployees = async () => {
    try {
      const response = await MainService.DriversInDropDown();
      setEmployees(response || []);
    } catch (err) {
      console.error('Error loading employees:', err);
    }
  };

  const loadCommissions = async () => {
    try {
      setLoading(true);
      setError('');
      
      // If "All" is selected, first get total count, then fetch all records
      if (pageSize === 'all') {
        // First request to get total count
        const countRequestData = {
          Skip: 0,
          Take: 1
        };
        const countResponse = await MainService.CommissionList(countRequestData);
        const total = Array.isArray(countResponse) ? countResponse.length : 0;
        
        if (total > 0) {
          // Now fetch all records - for now, we'll use a large number
          // Adjust based on your API's max limit
          const allRequestData = {
            Skip: 0,
            Take: 10000
          };
          const allResponse = await MainService.CommissionList(allRequestData);
          
          if (allResponse && Array.isArray(allResponse)) {
            setCommissions(allResponse);
            setTotalCount(allResponse.length);
          } else {
            setCommissions([]);
            setTotalCount(0);
          }
        } else {
          setCommissions([]);
          setTotalCount(0);
        }
      } else {
        const requestData = {
          Skip: (currentPage - 1) * pageSize,
          Take: pageSize
        };

        const response = await MainService.CommissionList(requestData);
        
        if (response && Array.isArray(response)) {
          setCommissions(response);
          setTotalCount(response.length);
        } else {
          setCommissions([]);
          setTotalCount(0);
        }
      }
    } catch (err) {
      console.error('Error loading commissions:', err);
      setError('Failed to load driver commissions');
      setCommissions([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setCommission(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleReset = () => {
    setCommission({
      Id: 0,
      PickUpValue: 0,
      DeliveryValue: 0,
      IsPickUpPercent: false,
      IsDeliveryPercent: false,
      IsByArea: false,
      EmployeeId: ''
    });
  };

  const handleSave = async () => {
    if (!commission.EmployeeId) {
      setError('Select Employee');
      return;
    }
    if (commission.DeliveryValue === 0) {
      setError('Enter Delivery Value');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const response = await MainService.SaveCommission(commission);
      
      if (response === 'Success' || response === true) {
        setSuccess('Driver Commission saved successfully');
        handleReset();
        loadCommissions();
      } else {
        setError(response || 'Failed to save driver commission');
      }
    } catch (err) {
      console.error('Error saving driver commission:', err);
      setError('Failed to save driver commission');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (commissionData) => {
    setCommission({
      Id: commissionData.Id || 0,
      PickUpValue: commissionData.PickUpValue || 0,
      DeliveryValue: commissionData.DeliveryValue || 0,
      IsPickUpPercent: commissionData.IsPickUpPercent || false,
      IsDeliveryPercent: commissionData.IsDeliveryPercent || false,
      IsByArea: commissionData.IsByArea || false,
      EmployeeId: commissionData.EmployeeId || commissionData.Employee?.Id || ''
    });
  };

  const handleDelete = (commissionData) => {
    setSelectedCommission(commissionData);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedCommission) return;

    try {
      setLoading(true);
      setError('');
      
      const response = await MainService.DeleteCommission(selectedCommission.Id);
      
      if (response === true || response === 'Success') {
        setSuccess('Driver Commission deleted successfully');
        setDeleteDialogOpen(false);
        setSelectedCommission(null);
        loadCommissions();
      } else {
        setError('Failed to delete driver commission');
      }
    } catch (err) {
      console.error('Error deleting driver commission:', err);
      setError('Failed to delete driver commission');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const exportToExcel = () => {
    const dataToExport = searchTerm ? filteredCommissions : commissions;
    
    if (dataToExport.length === 0) {
      setError('No commissions to export');
      return;
    }

    try {
      const exportData = dataToExport.map((comm, index) => ({
        '#': index + 1,
        'Employee': comm.Employee?.Name || 'N/A',
        'PickUp Value': comm.PickUpValue || 0,
        'Delivery Value': comm.DeliveryValue || 0,
        'IsPickUp Percent': comm.IsPickUpPercent ? 'Yes' : 'No',
        'IsDelivery Percent': comm.IsDeliveryPercent ? 'Yes' : 'No',
        'IsBy Area': comm.IsByArea ? 'Yes' : 'No'
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Driver Commissions');
      const fileName = `driver_commissions_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
      setSuccess('Excel file exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      setError('Failed to export Excel file');
    }
  };

  const filteredCommissions = commissions.filter(comm => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      comm.Employee?.Name?.toLowerCase().includes(searchLower) ||
      comm.PickUpValue?.toString().includes(searchLower) ||
      comm.DeliveryValue?.toString().includes(searchLower)
    );
  });

  const totalPages = pageSize === 'all' ? 1 : Math.ceil(filteredCommissions.length / pageSize);

  return (
    <div className="space-y-6">
      {/* Toast Messages */}
      {error && (
        <div className="fixed top-4 right-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="fixed top-4 right-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{success}</span>
          <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Form Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="mb-6 pb-4 border-b border-gray-200">
          <div className="flex items-center mb-2">
            <DollarSign className="w-6 h-6 text-[#46955A] mr-2" />
            <h2 className="text-xl font-semibold text-gray-900">
              Driver Commission
            </h2>
          </div>
          <p className="text-sm text-gray-500 ml-8">
            Manage driver commission rates and settings
          </p>
        </div>
        
        {/* Single Row - Employee (col-3), PickUp Value (col-2), Delivery Value (col-2), Checkboxes (col-3), Buttons (col-2) */}
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', md: 'repeat(12, 1fr)' }, 
          gap: 3 
        }}>
          {/* Employee - col-3 */}
          <Box sx={{ gridColumn: { xs: 'span 12', md: 'span 3' } }}>
            <SearchableSelect
              value={commission.EmployeeId}
              onChange={(value) => handleInputChange('EmployeeId', value)}
              options={employees}
              placeholder="Select Employee"
              required
              label="Employee"
            />
          </Box>
          {/* PickUp Value and Delivery Value - col-2, stacked vertically */}
          <Box sx={{ gridColumn: { xs: 'span 12', md: 'span 2' }, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="PickUp Value"
              type="number"
              value={commission.PickUpValue}
              onChange={(e) => handleInputChange('PickUpValue', parseFloat(e.target.value) || 0)}
              inputProps={{ step: '0.01', min: '0' }}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: '#46955A',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#46955A',
                  },
                },
              }}
            />
            <TextField
              fullWidth
              label="Delivery Value"
              type="number"
              value={commission.DeliveryValue}
              onChange={(e) => handleInputChange('DeliveryValue', parseFloat(e.target.value) || 0)}
              inputProps={{ step: '0.01', min: '0' }}
              required
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: '#46955A',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#46955A',
                  },
                },
              }}
            />
          </Box>
          {/* Checkboxes Column - col-3, stacked vertically */}
          <Box sx={{ gridColumn: { xs: 'span 12', md: 'span 3' }, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={commission.IsPickUpPercent}
                  onChange={(e) => handleInputChange('IsPickUpPercent', e.target.checked)}
                  sx={{
                    color: '#46955A',
                    '&.Mui-checked': {
                      color: '#46955A',
                    },
                  }}
                />
              }
              label="Is PickUp Percent"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={commission.IsDeliveryPercent}
                  onChange={(e) => handleInputChange('IsDeliveryPercent', e.target.checked)}
                  sx={{
                    color: '#46955A',
                    '&.Mui-checked': {
                      color: '#46955A',
                    },
                  }}
                />
              }
              label="Is Delivery Percent"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={commission.IsByArea}
                  onChange={(e) => handleInputChange('IsByArea', e.target.checked)}
                  sx={{
                    color: '#46955A',
                    '&.Mui-checked': {
                      color: '#46955A',
                    },
                  }}
                />
              }
              label="Is By Area"
            />
          </Box>
          {/* Buttons Column - col-2 */}
          <Box sx={{ gridColumn: { xs: 'span 12', md: 'span 2' }, display: 'flex', gap: 1, alignItems: 'flex-start', flexDirection: 'row', pt: 1 }}>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={loading}
              sx={{
                backgroundColor: '#46955A',
                minWidth: 'auto',
                width: '40px',
                height: '40px',
                padding: 0,
                '&:hover': { backgroundColor: '#3d7d4f' }
              }}
            >
              <Save className="w-5 h-5" />
            </Button>
            <Button
              variant="outlined"
              onClick={handleReset}
              disabled={loading}
              sx={{
                borderColor: '#46955A',
                color: '#46955A',
                minWidth: 'auto',
                width: '40px',
                height: '40px',
                padding: 0,
                '&:hover': { borderColor: '#3d7d4f', backgroundColor: '#f0f9f4' }
              }}
            >
              <RefreshCw className="w-5 h-5" />
            </Button>
          </Box>
        </Box>
      </div>

      {/* Table Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {/* Table Header with Search and Download */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <List className="w-5 h-5 text-[#46955A] mr-2" />
              <h3 className="text-lg font-semibold text-gray-900">Driver Commission List</h3>
            </div>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search commissions..."
                  value={searchTerm}
                  onChange={handleSearch}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#46955A] focus:border-transparent text-sm"
                />
              </div>
              <button
                onClick={exportToExcel}
                className="flex items-center justify-center w-10 h-10 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                title="Download to Excel"
              >
                <Download className="w-5 h-5 text-gray-600" />
              </button>
            </div>
          </div>
        </div>
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            height: '500px',
            position: 'relative',
            border: '1px solid #E5E7EB',
            borderRadius: '0.5rem',
            '& .MuiPaper-root': {
              boxShadow: 'none'
            }
          }}
        >
          {loading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.75)',
                backdropFilter: 'blur(4px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 20
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <CircularProgress size={32} sx={{ color: '#46955A' }} />
                <span style={{ fontSize: '0.875rem', color: '#6B7280' }}>Loading...</span>
              </Box>
            </Box>
          )}
          <Table stickyHeader sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.75rem', backgroundColor: '#F9FAFB', width: '5%', padding: '8px' }}>#</TableCell>
                <TableCell align="left" sx={{ fontWeight: 600, fontSize: '0.75rem', backgroundColor: '#F9FAFB', width: '20%', padding: '8px' }}>Employee</TableCell>
                <TableCell align="left" sx={{ fontWeight: 600, fontSize: '0.75rem', backgroundColor: '#F9FAFB', width: '12%', padding: '8px' }}>PickUp Value</TableCell>
                <TableCell align="left" sx={{ fontWeight: 600, fontSize: '0.75rem', backgroundColor: '#F9FAFB', width: '12%', padding: '8px' }}>Delivery Value</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.75rem', backgroundColor: '#F9FAFB', width: '12%', padding: '8px' }}>IsPickUp Percent</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.75rem', backgroundColor: '#F9FAFB', width: '12%', padding: '8px' }}>IsDelivery Percent</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.75rem', backgroundColor: '#F9FAFB', width: '12%', padding: '8px' }}>IsBy Area</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.75rem', backgroundColor: '#F9FAFB', width: '15%', padding: '8px' }}>Options</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredCommissions.length > 0 ? (
                filteredCommissions.map((comm, index) => (
                  <TableRow
                    key={comm.Id || index}
                    hover
                    sx={{
                      '&:hover': { backgroundColor: '#F9FAFB' },
                      '&:last-child td, &:last-child th': { border: 0 }
                    }}
                  >
                    <TableCell align="center" sx={{ fontSize: '0.875rem', color: '#6B7280', padding: '8px' }}>
                      {pageSize === 'all' 
                        ? index + 1 
                        : (currentPage - 1) * pageSize + index + 1}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#111827', padding: '8px' }}>
                      {comm.Employee?.Name || 'N/A'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem', color: '#374151', padding: '8px' }}>
                      {comm.PickUpValue || 0}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem', color: '#374151', padding: '8px' }}>
                      {comm.DeliveryValue || 0}
                    </TableCell>
                    <TableCell align="center" sx={{ padding: '8px' }}>
                      <Box
                        sx={{
                          display: 'inline-block',
                          px: 1.5,
                          py: 0.5,
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          fontWeight: 500,
                          backgroundColor: comm.IsPickUpPercent ? '#D1FAE5' : '#FEE2E2',
                          color: comm.IsPickUpPercent ? '#065F46' : '#991B1B'
                        }}
                      >
                        {comm.IsPickUpPercent ? 'Yes' : 'No'}
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={{ padding: '8px' }}>
                      <Box
                        sx={{
                          display: 'inline-block',
                          px: 1.5,
                          py: 0.5,
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          fontWeight: 500,
                          backgroundColor: comm.IsDeliveryPercent ? '#D1FAE5' : '#FEE2E2',
                          color: comm.IsDeliveryPercent ? '#065F46' : '#991B1B'
                        }}
                      >
                        {comm.IsDeliveryPercent ? 'Yes' : 'No'}
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={{ padding: '8px' }}>
                      <Box
                        sx={{
                          display: 'inline-block',
                          px: 1.5,
                          py: 0.5,
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          fontWeight: 500,
                          backgroundColor: comm.IsByArea ? '#D1FAE5' : '#FEE2E2',
                          color: comm.IsByArea ? '#065F46' : '#991B1B'
                        }}
                      >
                        {comm.IsByArea ? 'Yes' : 'No'}
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={{ padding: '8px' }}>
                      <div className="flex items-center justify-center gap-2">
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(comm)}
                            sx={{
                              color: '#46955A',
                              '&:hover': { backgroundColor: '#f0f9f4' }
                            }}
                          >
                            <Edit className="w-4 h-4" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(comm)}
                            sx={{
                              color: '#DC2626',
                              '&:hover': { backgroundColor: '#FEE2E2' }
                            }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </IconButton>
                        </Tooltip>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                      <DollarSign className="w-8 h-8 text-gray-400" />
                        <Box sx={{ color: '#6B7280' }}>
                          {searchTerm ? 'No commissions found matching your search' : 'No driver commissions available'}
                        </Box>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Footer with Pagination and Row Selection */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between', 
          alignItems: 'center',
          p: 2, 
          borderTop: '1px solid #E5E7EB',
          gap: 2
        }}>
          {/* Count Display */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2,
            flexWrap: 'wrap'
          }}>
              <span style={{ fontSize: '0.875rem', color: '#6B7280' }}>
                {filteredCommissions.length > 0 
                  ? pageSize === 'all'
                    ? `1-${filteredCommissions.length} out of ${filteredCommissions.length}`
                    : `${(currentPage - 1) * pageSize + 1}-${Math.min(currentPage * pageSize, filteredCommissions.length)} out of ${filteredCommissions.length}`
                  : '0 out of 0'
                }
              </span>
            
            {/* Rows per page selection */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <span style={{ fontSize: '0.875rem', color: '#6B7280' }}>Rows per page:</span>
              <FormControl size="small" sx={{ minWidth: 80 }}>
                <Select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(e.target.value);
                    setCurrentPage(1);
                  }}
                  sx={{
                    fontSize: '0.875rem',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#E5E7EB'
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#46955A'
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#46955A'
                    }
                  }}
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={20}>20</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                  <MenuItem value="all">All</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>

          {/* Pagination */}
          {totalPages > 1 && (
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              sx={{
                '& .MuiPaginationItem-root': {
                  color: '#6B7280'
                },
                '& .Mui-selected': {
                  backgroundColor: '#46955A !important',
                  color: '#fff !important',
                  '&:hover': {
                    backgroundColor: '#3d7d4f !important'
                  }
                },
                '& .MuiPaginationItem-root:hover': {
                  backgroundColor: '#f0f9f4'
                }
              }}
            />
          )}
        </Box>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete this driver commission? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ color: '#6B7280' }}>
            Cancel
          </Button>
          <Button onClick={confirmDelete} sx={{ color: '#DC2626' }} autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default DriverCommission;

